import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { GraduationCap, Users, UserCircle, FlaskConical, School, Loader2, Ban } from 'lucide-react';
import { schoolIsDemo } from './Schools';
import { formatDate } from '../utils/appDateTime';
import ConfirmSchoolNameDialog from './schools/ConfirmSchoolNameDialog';

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <Card className="border-border">
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground">{value ?? '—'}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="font-medium">{value || '—'}</p>
    </div>
  );
}

function formatAddress(school) {
  if (!school) return '';
  const parts = [school.address, school.city, school.state, school.pincode, school.country].filter(Boolean);
  return parts.join(', ');
}

function formatCreatedDate(value) {
  return formatDate(value, { day: '2-digit' });
}

function statusLabel(status) {
  if (!status) return '—';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export default function SchoolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enterSchoolObserver } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/schools/${id}/overview`)
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!data?.school) {
    return (
      <div className="space-y-4" data-testid="school-detail-page">
        <Link to="/schools" className="text-sm text-blue-600 hover:underline">← Back to schools</Link>
        <Card className="border-border">
          <CardContent className="py-12 text-center text-muted-foreground">
            School not found or you do not have access.
          </CardContent>
        </Card>
      </div>
    );
  }

  const { school, stats = {}, suggested_plan: suggestedPlan, subscription } = data;
  const demo = schoolIsDemo(school);
  const isActive = school.status === 'active';

  const handleGoToSchool = async () => {
    setEntering(true);
    try {
      const result = await enterSchoolObserver(school.id);
      if (!result.success) {
        toast({ title: 'Could not open school', description: result.message, variant: 'destructive' });
        return;
      }
      navigate('/dashboard');
    } finally {
      setEntering(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      await api.post(`schools/${school.id}/deactivate`, { confirm_name: school.name });
      toast({
        title: 'School deactivated',
        description: `${school.name} was moved to the Bin.`,
      });
      setDeactivateOpen(false);
      navigate('/bin?tab=schools');
    } catch (e) {
      toast({
        title: 'Deactivation failed',
        description: e.response?.data?.message || 'Failed to deactivate school',
        variant: 'destructive',
      });
    } finally {
      setDeactivating(false);
    }
  };

  const isDeactive = school.is_deactive === true || school.is_deactive === 1 || school.is_deactive === '1';

  return (
    <div className="space-y-6" data-testid="school-detail-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link to="/schools" className="text-sm text-blue-600 hover:underline">← Back to schools</Link>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <h1 className="text-2xl font-bold text-foreground">{school.name}</h1>
            {demo && (
              <Badge className="border-amber-500/50 bg-amber-500/15 text-amber-300 hover:bg-amber-500/20 gap-1">
                <FlaskConical className="w-3 h-3" />
                Demo school
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">School overview and statistics</p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {isActive && !isDeactive && (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={entering}
              onClick={handleGoToSchool}
              data-testid="go-to-school-btn"
            >
              {entering ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <School className="w-4 h-4 mr-2" />}
              Go to school
            </Button>
          )}
          {!isDeactive && (
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive border-destructive/40"
              onClick={() => setDeactivateOpen(true)}
            >
              <Ban className="w-4 h-4 mr-2" />
              Deactivate School
            </Button>
          )}
        </div>
      </div>

      <ConfirmSchoolNameDialog
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        schoolName={school.name}
        title="Deactivate this school?"
        description={
          <p>
            The school will be deactivated and moved to the Bin. Users of this school will
            not be able to log in or use the system until it is restored. Account status for
            individual users is unchanged. No data will be deleted.
          </p>
        }
        confirmLabel="Deactivate School"
        loading={deactivating}
        onConfirm={handleDeactivate}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard title="Total Students" value={stats.total_students ?? 0} icon={GraduationCap} color="bg-blue-600" />
        <StatCard title="Total Staff" value={stats.total_staff ?? 0} icon={Users} color="bg-indigo-600" />
        <StatCard title="Total Parents" value={stats.total_parents ?? 0} icon={UserCircle} color="bg-green-600" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>School Information</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
          <DetailRow label="School Name" value={school.name} />
          <DetailRow label="School Code" value={school.code} />
          <DetailRow label="Email" value={school.email} />
          <DetailRow label="Phone Number" value={school.phone} />
          <div className="sm:col-span-2 md:col-span-3">
            <DetailRow label="Address" value={formatAddress(school)} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Status</p>
            <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs ${isActive ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'}`}>
              {statusLabel(school.status)}
            </span>
          </div>
          <DetailRow label="Created Date" value={formatCreatedDate(school.created_at)} />
        </CardContent>
      </Card>

      {(suggestedPlan || subscription) && (
        <Card>
          <CardHeader>
            <CardTitle>Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {subscription && (
              <p className="text-sm">
                Current plan: <strong>{subscription.plan?.name || '—'}</strong>
                {' '}({subscription.status})
              </p>
            )}
            {suggestedPlan && (
              <p className="text-sm text-muted-foreground">
                CRM suggested plan: <Badge variant="outline">{suggestedPlan.name}</Badge>
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Subscriptions are managed by the school admin. Use assign API or school checkout to activate.
            </p>
            {suggestedPlan && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await api.post(`subscription/schools/${school.id}/assign`, {
                      plan_id: suggestedPlan.id,
                      billing_cycle: 'yearly',
                    });
                    toast({ title: 'Subscription assigned', description: `${suggestedPlan.name} assigned to school.` });
                    const res = await api.get(`/schools/${id}/overview`);
                    setData(res.data?.data || null);
                  } catch (e) {
                    toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
                  }
                }}
              >
                Assign suggested plan
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
